import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IntroPlaybackContext } from './IntroPlayback';
import { PageTransitionContext } from './PageTransition';
import './HomeIntro.css';

const INTRO_SEEN_KEY = 'bible-home-intro-seen';

export default function HomeIntro({ children }) {
  const [phase, setPhase] = useState(() => {
    try {
      return sessionStorage.getItem(INTRO_SEEN_KEY) === '1' ? 'done' : 'playing';
    } catch (_) {
      return 'playing';
    }
  });
  const [needsPlay, setNeedsPlay] = useState(false);
  const video = useRef(null);
  const { setActive } = useContext(IntroPlaybackContext);
  const pagePhase = useContext(PageTransitionContext);
  const finish = () => setPhase(current => current === 'playing' ? 'darken' : current);

  useLayoutEffect(() => {
    setActive(phase !== 'done');
    if (phase === 'playing') {
      // Count the first showing even if it is skipped or autoplay is blocked.
      try { sessionStorage.setItem(INTRO_SEEN_KEY, '1'); } catch (_) { /* Storage may be unavailable. */ }
    }
    return () => setActive(false);
  }, [phase, setActive]);

  useEffect(() => {
    if (phase === 'done') return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing' || pagePhase !== 'idle') return;
    let active = true;
    video.current.play().catch(() => { if (active) setNeedsPlay(true); });
    return () => { active = false; };
  }, [pagePhase, phase]);

  useEffect(() => {
    const next = { darken: ['hold', 800], hold: ['reveal', 300], reveal: ['done', 650] }[phase];
    if (!next) return;
    const timer = setTimeout(() => setPhase(next[0]), next[1]);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing') video.current?.pause();
  }, [phase]);

  const play = () => {
    setNeedsPlay(false);
    video.current.play().catch(() => setNeedsPlay(true));
  };

  return <>
    <div inert={phase !== 'done' ? '' : undefined}>{children}</div>
    {phase !== 'done' && createPortal(
      <div className={`home-intro home-intro--${phase}`} role="dialog" aria-modal="true" aria-label="聖書ASMR イントロ"
        onKeyDown={event => { if (event.key === 'Escape') finish(); }}>
        <video ref={video} src={`${process.env.PUBLIC_URL}/static/images/bible-asmr-intro.mp4?v=2`}
          playsInline preload="auto" onEnded={finish} onError={finish} aria-label="聖書ASMR イントロ動画" />
        {phase === 'playing' && <>
          {needsPlay && <button className="home-intro__play" onClick={play} autoFocus>イントロを再生 ▶</button>}
          <button className="home-intro__skip" onClick={finish}>スキップ</button>
        </>}
      </div>, document.body
    )}
  </>;
}
